import React, { useState } from "react";
import { useGetReportDataQuery } from "../services/api";
import { getApiV1Url } from "../services/apiConfig";
import { FiDownload, FiFileText, FiPackage, FiTrendingUp, FiTrendingDown, FiDollarSign, FiAlertCircle, FiBarChart2 } from "react-icons/fi";

function formatCurrency(value) {
  return new Intl.NumberFormat("en-ET", { style: "currency", currency: "ETB" }).format(value || 0);
}

export default function ReportsPage() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const { data, isLoading } = useGetReportDataQuery({ start_date: startDate, end_date: endDate });
  const report = data?.data || {};
  const summary = report.summary || {};
  const inventory = report.inventory || [];

  const handleDownloadExcel = () => {
    const params = new URLSearchParams();
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
    
    const apiUrl = getApiV1Url();
    const token = localStorage.getItem("roha_auth") ? JSON.parse(localStorage.getItem("roha_auth")).token : null;
    
    const url = `${apiUrl}/reports/export?${params.toString()}`;
    
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `Roha_Report_${startDate || "all"}_to_${endDate || "all"}.xlsx`;
        link.click();
      })
      .catch((err) => alert("Failed to download report: " + err.message));
  };

  if (isLoading) {
    return (
      <div style={{ background: "#F8F9FA", minHeight: "100vh", padding: "2rem" }}>
        <div style={{ marginBottom: "2rem" }}>
          <div style={{ 
            height: "32px", 
            width: "180px", 
            background: "#E2E8F0", 
            borderRadius: "6px",
            marginBottom: "0.75rem",
            animation: "pulse 1.5s ease-in-out infinite"
          }}></div>
        </div>
        <div style={{ 
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1.25rem"
        }}>
          {[1,2,3,4].map(i => (
            <div key={i} style={{
              background: "white",
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              padding: "1.5rem",
              animation: "pulse 1.5s ease-in-out infinite"
            }}>
              <div style={{ height: "16px", width: "60%", background: "#E2E8F0", borderRadius: "4px", marginBottom: "1rem" }}></div>
              <div style={{ height: "32px", width: "40%", background: "#E2E8F0", borderRadius: "4px" }}></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#F8F9FA", minHeight: "100vh", padding: "2rem" }}>
      {/* Page Header */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center", 
        marginBottom: "2rem" 
      }}>
        <div>
          <h1 style={{ 
            fontSize: "2rem", 
            marginBottom: "0.5rem", 
            fontWeight: 600,
            color: "#1E293B"
          }}>
            Reports & Analytics
          </h1>
          <p style={{ color: "#64748B", fontSize: "0.95rem" }}>
            Tax-ready financial reports with Ethiopian calendar
          </p>
        </div>
        <button 
          onClick={handleDownloadExcel}
          style={{
            padding: "0.75rem 1.5rem",
            background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "0.95rem",
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
            transition: "all 0.2s"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(16, 185, 129, 0.4)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(16, 185, 129, 0.3)";
          }}
        >
          <FiDownload /> Download Excel
        </button>
      </div>

      {/* Date Filter Card */}
      <div style={{
        background: "white",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        padding: "1.5rem",
        marginBottom: "2rem"
      }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "1rem", alignItems: "end" }}>
          <div>
            <label style={{ 
              display: "block", 
              fontSize: "0.875rem", 
              fontWeight: 600, 
              color: "#1E293B", 
              marginBottom: "0.5rem" 
            }}>
              Start Date
            </label>
            <input 
              type="date" 
              value={startDate} 
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                width: "100%",
                padding: "0.625rem 0.875rem",
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                fontSize: "0.95rem",
                color: "#1E293B",
                transition: "all 0.2s"
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = "#3B82F6"}
              onBlur={(e) => e.currentTarget.style.borderColor = "#E2E8F0"}
            />
          </div>
          <div>
            <label style={{ 
              display: "block", 
              fontSize: "0.875rem", 
              fontWeight: 600, 
              color: "#1E293B", 
              marginBottom: "0.5rem" 
            }}>
              End Date
            </label>
            <input 
              type="date" 
              value={endDate} 
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                width: "100%",
                padding: "0.625rem 0.875rem",
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                fontSize: "0.95rem",
                color: "#1E293B",
                transition: "all 0.2s"
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = "#3B82F6"}
              onBlur={(e) => e.currentTarget.style.borderColor = "#E2E8F0"}
            />
          </div>
          <button 
            onClick={() => { setStartDate(""); setEndDate(""); }}
            style={{
              padding: "0.625rem 1.5rem",
              background: "transparent",
              border: "1px solid #E2E8F0",
              borderRadius: "8px",
              fontSize: "0.95rem",
              fontWeight: 600,
              color: "#64748B",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#F8FAFC";
              e.currentTarget.style.borderColor = "#CBD5E1";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.borderColor = "#E2E8F0";
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {/* Main KPI Cards */}
      <div style={{ 
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: "1.25rem",
        marginBottom: "2rem"
      }}>
        {/* Total Revenue */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              💰
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Total Revenue
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#10B981" }}>
            {formatCurrency(summary.total_revenue)}
          </div>
        </div>

        {/* Total Refunds */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              ↩️
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Total Refunds
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#F59E0B" }}>
            -{formatCurrency(summary.total_refunds)}
          </div>
        </div>

        {/* Total Expenses */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              💸
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Total Expenses
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#EF4444" }}>
            -{formatCurrency(summary.total_expenses)}
          </div>
        </div>

        {/* Net Profit */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: summary.net_profit >= 0 
                ? "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)" 
                : "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              {summary.net_profit >= 0 ? "📈" : "📉"}
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Net Profit
          </div>
          <div style={{ 
            fontSize: "1.75rem", 
            fontWeight: 700, 
            color: summary.net_profit >= 0 ? "#8B5CF6" : "#EF4444"
          }}>
            {formatCurrency(summary.net_profit)}
          </div>
        </div>
      </div>

      {/* Summary Cards Grid */}
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", 
        gap: "1.25rem",
        marginBottom: "2rem"
      }}>
        {/* Sales Summary */}
        <div style={{
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem"
        }}>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "0.75rem", 
            marginBottom: "1.25rem",
            paddingBottom: "1rem",
            borderBottom: "1px solid #F1F5F9"
          }}>
            <div style={{
              width: "40px",
              height: "40px",
              borderRadius: "8px",
              background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <FiFileText color="white" size={20} />
            </div>
            <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B" }}>
              Sales Summary
            </h3>
          </div>
          <div style={{ background: "#F8FAFC", borderRadius: "8px", padding: "1rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.75rem" }}>
              <span style={{ color: "#64748B", fontSize: "0.9rem" }}>Total Sales:</span>
              <span style={{ fontWeight: 700, color: "#1E293B" }}>{report.sales?.length || 0}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.75rem" }}>
              <span style={{ color: "#64748B", fontSize: "0.9rem" }}>Revenue:</span>
              <span style={{ fontWeight: 700, color: "#10B981" }}>{formatCurrency(summary.total_revenue)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#64748B", fontSize: "0.9rem" }}>Average Sale:</span>
              <span style={{ fontWeight: 700, color: "#8B5CF6" }}>
                {formatCurrency(report.sales?.length ? summary.total_revenue / report.sales.length : 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Returns Summary */}
        <div style={{
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem"
        }}>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "0.75rem", 
            marginBottom: "1.25rem",
            paddingBottom: "1rem",
            borderBottom: "1px solid #F1F5F9"
          }}>
            <div style={{
              width: "40px",
              height: "40px",
              borderRadius: "8px",
              background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <FiAlertCircle color="white" size={20} />
            </div>
            <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B" }}>
              Returns Summary
            </h3>
          </div>
          <div style={{ background: "#FEF3C7", borderRadius: "8px", padding: "1rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.75rem" }}>
              <span style={{ color: "#92400E", fontSize: "0.9rem" }}>Total Returns:</span>
              <span style={{ fontWeight: 700, color: "#1E293B" }}>{report.returns?.length || 0}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.75rem" }}>
              <span style={{ color: "#92400E", fontSize: "0.9rem" }}>Refund Amount:</span>
              <span style={{ fontWeight: 700, color: "#EF4444" }}>{formatCurrency(summary.total_refunds)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#92400E", fontSize: "0.9rem" }}>Return Rate:</span>
              <span style={{ fontWeight: 700, color: "#1E293B" }}>
                {report.sales?.length ? ((report.returns?.length || 0) / report.sales.length * 100).toFixed(1) : 0}%
              </span>
            </div>
          </div>
        </div>

        {/* Expenses Summary */}
        <div style={{
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem"
        }}>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "0.75rem", 
            marginBottom: "1.25rem",
            paddingBottom: "1rem",
            borderBottom: "1px solid #F1F5F9"
          }}>
            <div style={{
              width: "40px",
              height: "40px",
              borderRadius: "8px",
              background: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <FiDollarSign color="white" size={20} />
            </div>
            <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B" }}>
              Expenses Summary
            </h3>
          </div>
          <div style={{ background: "#FEF2F2", borderRadius: "8px", padding: "1rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.75rem" }}>
              <span style={{ color: "#991B1B", fontSize: "0.9rem" }}>Total Expenses:</span>
              <span style={{ fontWeight: 700, color: "#1E293B" }}>{report.expenses?.length || 0}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.75rem" }}>
              <span style={{ color: "#991B1B", fontSize: "0.9rem" }}>Amount:</span>
              <span style={{ fontWeight: 700, color: "#EF4444" }}>{formatCurrency(summary.total_expenses)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#991B1B", fontSize: "0.9rem" }}>Expense Ratio:</span>
              <span style={{ fontWeight: 700, color: "#1E293B" }}>
                {summary.total_revenue ? ((summary.total_expenses / summary.total_revenue) * 100).toFixed(1) : 0}%
              </span>
            </div>
          </div>
        </div>

        {/* Profit Summary */}
        <div style={{
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem"
        }}>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "0.75rem", 
            marginBottom: "1.25rem",
            paddingBottom: "1rem",
            borderBottom: "1px solid #F1F5F9"
          }}>
            <div style={{
              width: "40px",
              height: "40px",
              borderRadius: "8px",
              background: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <FiBarChart2 color="white" size={20} />
            </div>
            <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B" }}>
              Profit Summary
            </h3>
          </div>
          <div style={{ background: "#F5F3FF", borderRadius: "8px", padding: "1rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.75rem" }}>
              <span style={{ color: "#5B21B6", fontSize: "0.9rem" }}>Net Income:</span>
              <span style={{ fontWeight: 700, color: "#8B5CF6" }}>{formatCurrency(summary.net_income)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.75rem" }}>
              <span style={{ color: "#5B21B6", fontSize: "0.9rem" }}>Net Profit:</span>
              <span style={{ 
                fontWeight: 700, 
                color: summary.net_profit >= 0 ? "#8B5CF6" : "#EF4444" 
              }}>
                {formatCurrency(summary.net_profit)}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#5B21B6", fontSize: "0.9rem" }}>Profit Margin:</span>
              <span style={{ fontWeight: 700, color: "#8B5CF6" }}>
                {summary.total_revenue ? ((summary.net_profit / summary.total_revenue) * 100).toFixed(1) : 0}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Inventory Summary Section */}
      {inventory.length > 0 && (
        <div style={{
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          marginBottom: "2rem"
        }}>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "0.75rem", 
            marginBottom: "1.25rem"
          }}>
            <div style={{
              width: "40px",
              height: "40px",
              borderRadius: "8px",
              background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <FiPackage color="white" size={20} />
            </div>
            <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B" }}>
              Inventory Summary by Material
            </h3>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ 
              width: "100%", 
              borderCollapse: "collapse",
              fontSize: "0.9rem",
              minWidth: "800px"
            }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #E2E8F0" }}>
                  <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Material Type</th>
                  <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Unit</th>
                  <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Total Registered</th>
                  <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Total Sold (All Time)</th>
                  <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Current Remaining</th>
                  {(startDate || endDate) && (
                    <>
                      <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Added in Period</th>
                      <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Sold in Period</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {inventory.map((item, idx) => (
                  <tr key={idx} style={{ 
                    borderBottom: "1px solid #F1F5F9",
                    transition: "all 0.2s"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#F8FAFC";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "white";
                  }}
                  >
                    <td style={{ padding: "1rem 0.75rem" }}>
                      <span style={{
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        background: "#FEF3C7",
                        color: "#92400E",
                        textTransform: "capitalize"
                      }}>
                        {item.material_type}
                      </span>
                    </td>
                    <td style={{ padding: "1rem 0.75rem", color: "#64748B" }}>{item.unit}</td>
                    <td style={{ padding: "1rem 0.75rem", fontWeight: 600, color: "#3B82F6" }}>
                      <FiTrendingUp style={{ display: "inline", marginRight: "4px", verticalAlign: "middle" }} />
                      {Number(item.total_registered || 0).toFixed(2)} {item.unit}
                    </td>
                    <td style={{ padding: "1rem 0.75rem", fontWeight: 600, color: "#EF4444" }}>
                      <FiTrendingDown style={{ display: "inline", marginRight: "4px", verticalAlign: "middle" }} />
                      {Number(item.total_sold_alltime || 0).toFixed(2)} {item.unit}
                    </td>
                    <td style={{ padding: "1rem 0.75rem", fontWeight: 700, fontSize: "1.05rem", color: "#8B5CF6" }}>
                      {Number(item.current_remaining || 0).toFixed(2)} {item.unit}
                    </td>
                    {(startDate || endDate) && (
                      <>
                        <td style={{ padding: "1rem 0.75rem", fontWeight: 600, color: "#10B981" }}>
                          +{Number(item.added_in_period || 0).toFixed(2)} {item.unit}
                        </td>
                        <td style={{ padding: "1rem 0.75rem", fontWeight: 600, color: "#F59E0B" }}>
                          -{Number(item.sold_in_period || 0).toFixed(2)} {item.unit}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Download Excel CTA */}
      <div style={{
        background: "white",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        padding: "2.5rem",
        textAlign: "center"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "12px",
          background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 1.5rem"
        }}>
          <FiDownload color="white" size={32} />
        </div>
        <h3 style={{ 
          fontSize: "1.5rem", 
          fontWeight: 600, 
          color: "#1E293B", 
          marginBottom: "0.75rem" 
        }}>
          Tax-Ready Excel Report
        </h3>
        <p style={{ 
          color: "#64748B", 
          fontSize: "0.95rem", 
          marginBottom: "1.5rem",
          maxWidth: "600px",
          margin: "0 auto 1.5rem"
        }}>
          Download comprehensive report with Sales, Returns, Expenses, Stock Movements, and Profit Summary.
          Includes Ethiopian Calendar dates for tax compliance.
        </p>
        <button 
          onClick={handleDownloadExcel}
          style={{
            padding: "0.875rem 2rem",
            background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "1rem",
            fontWeight: 600,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
            transition: "all 0.2s"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(16, 185, 129, 0.4)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(16, 185, 129, 0.3)";
          }}
        >
          <FiDownload size={20} /> Download Excel Report
        </button>
      </div>
    </div>
  );
}
